import { useDashboard } from '../../hooks/useDashboard';
import { useCache } from '../../hooks/useCache';
import { fetchTrafficDensity } from '../../api';
import { ChartSkeleton } from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import styles from './Charts.module.css';

function cellColor(v, max) {
  if (v === 0) return null;
  const t = v / max;
  if (t < 0.10) return 'rgba(180,215,240,0.5)';
  if (t < 0.25) return '#b8d9f0';
  if (t < 0.45) return '#7ec8e3';
  if (t < 0.65) return '#4aa8d0';
  if (t < 0.80) return '#2b80b8';
  return '#1a5fa0';
}

function fmtV(v) {
  if (v === 0) return '0';
  return v >= 1000 ? (v / 1000).toFixed(0) + 'k' : String(v);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_DAYS = { Sun: 'Sunday', Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday' };

function fmtRangeDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]}, ${y}`;
}

function datesByWeekday(fromIso, toIso) {
  const slots = ['', '', '', '', '', '', ''];
  if (!fromIso || !toIso) return slots;
  const [fy, fm, fd] = fromIso.split('-').map(Number);
  const [ty, tm, td] = toIso.split('-').map(Number);
  const cur = new Date(fy, fm - 1, fd);
  const end = new Date(ty, tm - 1, td);
  let guard = 0;
  while (cur <= end && guard < 14) {
    slots[cur.getDay()] = `${String(cur.getDate()).padStart(2, '0')} ${MONTHS[cur.getMonth()]}`;
    cur.setDate(cur.getDate() + 1);
    guard++;
  }
  return slots;
}

export default function TrafficDensity() {
  const { currentStore, visibleStores, endDate } = useDashboard();
  const storeIds = currentStore
    ? currentStore.storeId
    : visibleStores.map(s => s.storeId).join(',');

  const cacheKey = storeIds ? `traffic:${storeIds}:${endDate}` : null;
  const { data, loading } = useCache(cacheKey, () => fetchTrafficDensity(storeIds, endDate, 7));

  const matrix = data?.matrix || [];
  const hourLabels = data?.hourLabels || [];
  const dayLabels = data?.dayLabels || [];

  const sub = data?.fromDate && data?.toDate
    ? `Based on ${fmtRangeDate(data.fromDate)} – ${fmtRangeDate(data.toDate)}`
    : '';

  const dayDates = datesByWeekday(data?.fromDate, data?.toDate);

  const max = matrix.length > 0 ? Math.max(...matrix.flat(), 1) : 1;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>Traffic Density</div>
          <div className={styles.sub}>{sub}</div>
        </div>
      </div>
      {loading ? (
        <ChartSkeleton height={200} />
      ) : matrix.length === 0 ? (
        <EmptyState message="No traffic data available" />
      ) : (
        <div className={styles.tdLayout}>
          <div className={styles.yLabel}>Hours</div>
          <div style={{ flex: 1, overflowX: 'auto', paddingBottom: 12 }}>
            <table className={styles.tdTable}>
              <colgroup>
                <col style={{ width: 40 }} />
                {dayLabels.map((_, i) => <col key={i} />)}
              </colgroup>
              <tbody>
                {matrix.map((row, ri) => (
                  <tr key={ri}>
                    <th className={styles.rowHdr}>{hourLabels[ri]}</th>
                    {row.map((v, ci) => {
                      const bg = cellColor(v, max);
                      return (
                        <td
                          key={ci}
                          className={v === 0 ? styles.zero : undefined}
                          style={{
                            textAlign: 'center', fontSize: 11, fontWeight: 600,
                            padding: '4px 2px', borderRadius: 0,
                            background: bg || (v === 0 ? 'var(--g100)' : undefined),
                            color: bg ? '#fff' : (v === 0 ? 'var(--g400)' : 'var(--g700)'),
                          }}
                        >
                          {fmtV(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th className={styles.rowHdr}></th>
                  {dayLabels.map((d, i) => (
                    <th key={i} className={styles.dayFoot}>
                      <div>{FULL_DAYS[d] || d}</div>
                      {dayDates[i] && <div className={styles.dayDate}>{dayDates[i]}</div>}
                    </th>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
